import { useState, useCallback } from 'react';
import { CircularProgress } from '../CircularProgress';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { performanceColor } from '../scheduled/shared';
import { KPICell, SectionFrame, PERF_MONEY_CLASS, rejectedFoot } from './perfCells';
import { useQualityMeasureLabel } from '../../../lib/qualityLabels';
import { usePreferences } from '../../../contexts/PreferencesContext';
import type { POLineDrilldownMode } from '../../../lib/api/po-line-dashboard';
import { POLineDrilldownModal } from './POLineDrilldownModal';
import { deliveryTabCounts, type POLinePeriodProps, type POLineDrilldownState } from './types';
import { blockTarget } from '../periodTarget';
import { showsOtd, showsOtif } from '../deliveryMetricMode';

const PALETTE = {
  onTime: '#0f766e',
  early:  '#2563eb',
  late:   '#dc2626',
  pending: '#cbd5e1',
};

/**
 * Current Month panel of the PO Line Dashboard — the Current Week panel over a
 * calendar month. Same tile frames, same donuts; the bar chart is one stacked
 * bar per week of the month (Mon–Sun, clamped to the month) instead of per day.
 * Every figure drills through ``/analytics/po-line-dashboard/drilldown/`` with
 * ``start``/``end`` = the month window (or the bar's own week).
 */
export function POLineCurrentMonth({ data, formatCurrency, filters, onRefresh }: POLinePeriodProps) {
  const cm = data.current_month;
  const tabCounts = deliveryTabCounts(cm.delivery);
  const qualityLabel = useQualityMeasureLabel();
  const { preferences } = usePreferences();
  const kpiDecimals = preferences.kpi_percent_decimals ?? 0;

  // CR-6266: the target at the end of the tile's own window. CR-7714 FE-20:
  // no target set means no colour, never an invented 95.
  const otifTarget = blockTarget(cm, 'otif', data.otif_target ?? null);
  const otdTarget = blockTarget(cm, 'otd', data.otd_target ?? null);
  const deliveryMode = data.delivery_metric_mode;  // CR-8697: the helpers know 'none'
  const showOTIF = showsOtif(deliveryMode);
  const showOTD  = showsOtd(deliveryMode);

  const isLive = cm.is_live ?? true;
  const periodWord = isLive ? 'this' : 'that';
  const monthStartIso = cm.window_start;
  const monthEndIso   = cm.window_end;
  const otdStartIso   = cm.otif_window_start;
  const otdEndIso     = cm.otif_window_end;
  const toDateLabel   = isLive ? 'Month to date' : 'Full month';
  const monthLabel    = isLive ? 'This month' : cm.month_label;

  const [modal, setModal] = useState<POLineDrilldownState | null>(null);
  const openMonth = useCallback((mode: POLineDrilldownMode) => {
    setModal({ mode, periodStart: monthStartIso, periodEnd: monthEndIso, periodGrain: 'month', periodLabel: monthLabel, tabCounts });
  }, [monthStartIso, monthEndIso, monthLabel, tabCounts]);
  const openOtdWindow = useCallback((mode: POLineDrilldownMode) => {
    setModal({ mode, periodStart: otdStartIso, periodEnd: otdEndIso, periodGrain: 'month', periodLabel: toDateLabel, tabCounts, tabContext: 'otd_mix' });
  }, [otdStartIso, otdEndIso, toDateLabel, tabCounts]);
  const openWeek = useCallback((mode: POLineDrilldownMode, bar: { start: string; end: string; onTime: number; early: number; late: number; pending: number }) => {
    setModal({
      mode, periodStart: bar.start, periodEnd: bar.end, periodGrain: 'week',
      tabCounts: { grn_on_time: bar.onTime, grn_early: bar.early, grn_late: bar.late, open: bar.pending },
      tabContext: 'bars',
    });
  }, []);

  const deliveriesByWeek = cm.deliveries_by_week.map((w) => ({
    week: w.label, start: w.week_start, end: w.week_end,
    onTime: w.on_time, early: w.early, late: w.late, pending: w.pending,
  }));

  const otdSplit = [
    { name: 'On time', value: cm.delivery.on_time, color: PALETTE.onTime, mode: 'grn_on_time' as const },
    { name: 'Early',   value: cm.delivery.early,   color: PALETTE.early,  mode: 'grn_early' as const },
    { name: 'Late',    value: cm.delivery.late,    color: PALETTE.late,   mode: 'grn_late' as const },
  ];
  const otdEmpty = otdSplit.every((d) => d.value === 0);
  const otdData: Array<{ name: string; value: number; color: string; mode: POLineDrilldownMode | null }> = otdEmpty
    ? [{ name: 'No data yet', value: 1, color: '#f1f5f9', mode: null }]
    : otdSplit;

  const otifPass    = cm.delivery.passed  ?? 0;
  const otifPending = cm.delivery.pending ?? 0;
  const otifFail    = cm.delivery.failed  ?? 0;
  // CR-6327: arrears are their own outcome. They used to be counted inside
  // Failed, so the slice accused suppliers of failing lines they simply had
  // not delivered yet. Amber, not red — nothing has been judged.
  const otifArrears = cm.delivery.arrears ?? 0;
  const otifSplit = [
    { name: 'Passed',  value: otifPass,    color: PALETTE.onTime, mode: 'otif_passed' as const },
    { name: 'Pending', value: otifPending, color: '#94a3b8',      mode: 'otif_pending' as const },
    { name: 'Arrears', value: otifArrears, color: '#f59e0b',      mode: 'otif_arrears' as const },
    { name: 'Failed',  value: otifFail,    color: '#dc2626',      mode: 'otif_failed' as const },
  ];
  const otifEmpty = otifSplit.every((d) => d.value === 0);
  const otifData: Array<{ name: string; value: number; color: string; mode: POLineDrilldownMode | null }> = otifEmpty
    ? [{ name: 'No data yet', value: 1, color: '#f1f5f9', mode: null }]
    : otifSplit;

  // Per-segment click handler — BarChart.onClick always reports the first
  // series, so each <Bar> resolves its own week from the clicked payload.
  const onSegmentClick = (segment: 'onTime' | 'early' | 'late' | 'pending') => (raw: unknown) => {
    const payload = raw && typeof raw === 'object'
      ? ((raw as { payload?: unknown }).payload ?? raw)
      : undefined;
    const rec = payload && typeof payload === 'object' ? (payload as { start?: unknown }) : undefined;
    const bar = rec && typeof rec.start === 'string' ? deliveriesByWeek.find((w) => w.start === rec.start) : undefined;
    if (!bar) return;
    if (segment === 'onTime')       openWeek('grn_on_time', bar);
    else if (segment === 'early')   openWeek('grn_early', bar);
    else if (segment === 'late')    openWeek('grn_late', bar);
    else if (segment === 'pending') openWeek('open', bar);
  };

  const handleOtdSlice = (slice: unknown) => {
    const m = slice && typeof slice === 'object' ? (slice as { mode?: unknown }).mode : undefined;
    if (typeof m === 'string') openOtdWindow(m as POLineDrilldownMode);
  };
  const handleOtifSlice = (slice: unknown) => {
    const m = slice && typeof slice === 'object' ? (slice as { mode?: unknown }).mode : undefined;
    if (typeof m === 'string') openMonth(m as POLineDrilldownMode);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <SectionFrame title="Quality" accent="red">
          <div className="grid grid-cols-2 gap-3">
            <KPICell
              label={qualityLabel}
              value={<span className="text-2xl font-semibold tabular-nums">{cm.quality.ppm.toLocaleString()}</span>}
              foot={rejectedFoot(cm.quality.reject_qty)}
              onClick={() => openMonth('ppm')}
            />
            <KPICell
              label="NCRs"
              value={<span className="text-2xl font-semibold tabular-nums">{cm.quality.ncrs.toLocaleString()}</span>}
              onClick={() => openMonth('ncrs')}
            />
          </div>
        </SectionFrame>

        <SectionFrame title="Delivery" accent="green">
          <div className={`grid gap-3 items-start ${showOTIF && showOTD ? 'grid-cols-3' : 'grid-cols-2'}`}>
            {showOTIF && (
              <KPICell
                label="OTIF"
                value={<CircularProgress percentage={cm.delivery.otif_pct} size={80} strokeWidth={6} color={performanceColor(cm.delivery.otif_pct, otifTarget)} decimals={kpiDecimals} />}
                foot={`${cm.delivery.otif_met}/${cm.delivery.total_due} PO lines`}
                onClick={() => openMonth('otif_due')}
              />
            )}
            {showOTD && (
              <KPICell
                label="OTD"
                value={<CircularProgress percentage={cm.delivery.otd_pct} size={80} strokeWidth={6} color={performanceColor(cm.delivery.otd_pct, otdTarget)} decimals={kpiDecimals} />}
                foot={`${cm.delivery.otd_met}/${cm.delivery.otd_due ?? cm.delivery.total_due} receipts on time`}
                // Customer review 11 Sep (item 31): open on the denominator
                // the caption divides by (All / On time / Not on time).
                onClick={() => openMonth('otd_due')}
              />
            )}
            <KPICell
              label="Appeals"
              value={<span className="text-2xl font-semibold tabular-nums">{cm.delivery.appeals}</span>}
              onClick={() => openMonth('appeals')}
            />
          </div>
        </SectionFrame>

        <SectionFrame title="Cost" accent="blue">
          <div className="grid grid-cols-3 gap-3">
            {/* CR-9074: Open PO Lines and Arrears headline the LINE COUNT — the
                number their drill-downs list — with the value underneath. A
                money headline read "0" over "4 of 4 lines" whenever the lines
                carried no price, and was taken for a count of zero. */}
            <KPICell
              label="Open PO Lines"
              value={<span className="text-2xl font-semibold tabular-nums">{cm.cost.open_lines.toLocaleString()}</span>}
              foot={`of ${cm.cost.total_lines.toLocaleString()} lines · ${formatCurrency(cm.cost.open_value)}`}
              onClick={() => openMonth('open')}
            />
            <KPICell
              label="Received"
              value={<span className={PERF_MONEY_CLASS}>{formatCurrency(cm.cost.received_value)}</span>}
              foot={`of ${formatCurrency(cm.cost.total_value)} committed`}
              onClick={() => openMonth('received')}
            />
            <KPICell
              label="Arrears"
              value={<span className="text-2xl font-semibold tabular-nums">{cm.cost.arrears_lines.toLocaleString()}</span>}
              foot={`past-due lines · ${formatCurrency(cm.cost.arrears_value)}`}
              onClick={() => setModal({ mode: 'arrears', periodEnd: data.today, periodLabel: 'All past-due' })}
            />
          </div>
        </SectionFrame>
      </div>

      <div className={`grid gap-4 ${showOTIF && showOTD ? 'grid-cols-3' : (showOTIF || showOTD) ? 'grid-cols-2' : 'grid-cols-1'}`}>
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">Deliveries by Week</CardTitle>
            <p className="text-[11px] text-muted-foreground">Weeks of {cm.month_label} • {periodWord} month — click a segment to drill in</p>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={deliveriesByWeek} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="week" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: '#e2e8f0', fillOpacity: 0.7 }} contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                  <Bar dataKey="onTime"  stackId="a" fill={PALETTE.onTime} name="On time" cursor="pointer" activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }} onClick={onSegmentClick('onTime')} />
                  <Bar dataKey="early"   stackId="a" fill={PALETTE.early}  name="Early"   cursor="pointer" activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }} onClick={onSegmentClick('early')} />
                  <Bar dataKey="late"    stackId="a" fill={PALETTE.late}   name="Late"    cursor="pointer" activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }} onClick={onSegmentClick('late')} />
                  <Bar dataKey="pending" stackId="a" fill={PALETTE.pending} name="Pending" radius={[3, 3, 0, 0]} cursor="pointer" activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }} onClick={onSegmentClick('pending')} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {showOTD && (
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-700">OTD Mix</CardTitle>
              <p className="text-[11px] text-muted-foreground">{toDateLabel} • {(cm.delivery.total_otd ?? cm.delivery.total_with_status).toLocaleString()} receipts{(cm.delivery.total_otd ?? 0) > cm.delivery.total_with_status ? ` (${((cm.delivery.total_otd ?? 0) - cm.delivery.total_with_status).toLocaleString()} not yet judged)` : ''} — click a slice to drill in</p>
            </CardHeader>
            <CardContent>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={otdData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={56} outerRadius={84}
                         paddingAngle={otdEmpty ? 0 : 2} stroke="none" onClick={handleOtdSlice}
                         activeShape={{ stroke: '#0f172a', strokeWidth: 1.5 } as unknown as Record<string, unknown>}>
                      {otdData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} cursor={entry.mode ? 'pointer' : 'default'} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {showOTIF && (
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-700">OTIF Mix</CardTitle>
              <p className="text-[11px] text-muted-foreground">{isLive ? 'This month' : 'That month'} • {(otifPass + otifPending + otifArrears + otifFail).toLocaleString()} PO lines — click a slice to drill in</p>
            </CardHeader>
            <CardContent>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={otifData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={56} outerRadius={84}
                         paddingAngle={otifEmpty ? 0 : 2} stroke="none" onClick={handleOtifSlice}
                         activeShape={{ stroke: '#0f172a', strokeWidth: 1.5 } as unknown as Record<string, unknown>}>
                      {otifData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} cursor={entry.mode ? 'pointer' : 'default'} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {modal && (
        <POLineDrilldownModal
          open
          onClose={() => setModal(null)}
          mode={modal.mode}
          filters={filters}
          periodStart={modal.periodStart}
          periodEnd={modal.periodEnd}
          periodGrain={modal.periodGrain}
          periodLabel={modal.periodLabel}
          currency={data.currency}
          tabCounts={modal.tabCounts}
          tabContext={modal.tabContext}
          onDataChanged={onRefresh}
        />
      )}
    </div>
  );
}
